import React, { useState } from 'react';

// QR Code component - In production, use qrcode.react library
const QRCode = ({ value, size = 200 }) => {
  // Check if value is a base64 image
  if (value && value.startsWith('data:image/')) {
    return (
      <div 
        className="qr-code-container"
        style={{
          width: size,
          height: size,
          backgroundColor: 'white',
          border: '1px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          overflow: 'hidden'
        }}
      >
        <img 
          src={value} 
          alt="QR Code" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </div>
    );
  }

  // Generate a simple QR pattern for demo - replace with actual QR library
  const generateQRPattern = () => {
    const pattern = [];
    const val = value || '';
    for (let i = 0; i < 25; i++) {
      const row = [];
      for (let j = 0; j < 25; j++) {
        // Create a pseudo-random pattern based on position and value
        const hash = (i * 25 + j + val.length) % 7;
        row.push(hash < 3 ? '█' : ' ');
      }
      pattern.push(row.join(''));
    }
    return pattern.join('\n');
  };

  return (
    <div 
      className="qr-code-container"
      style={{
        width: size,
        height: size,
        backgroundColor: 'white',
        border: '1px solid #ddd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'monospace',
        fontSize: '6px',
        lineHeight: '6px',
        whiteSpace: 'pre',
        overflow: 'hidden',
        borderRadius: '4px'
      }}
    >
      <div style={{ transform: 'scale(0.8)' }}>
        {generateQRPattern()}
      </div>
    </div>
  );
};

const TwoFactorQRModal = ({ 
  show, 
  onHide, 
  qrCodeValue, 
  onConfirm = () => {}, 
  onClose = () => {},
  onSendEmailOtp = () => {},
  isSendingEmailOtp = false, // Add this prop
  viaEmail = false
}) => {
  const [isScanned, setIsScanned] = useState(false);
  const [mode, setMode] = useState(viaEmail ? 'email' : 'authenticator');

  if (!show) return null;

  const handleConfirm = () => {
    if (isScanned) {
      onConfirm();
      onHide();
      setIsScanned(false);
    }
  };

  const handleClose = () => {
    onClose();
    onHide();
    setIsScanned(false);
    setMode('authenticator'); // Reset mode
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle Send Email OTP click with protection against double clicks
  const handleSendEmailOtpClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double clicks
    if (isSendingEmailOtp) {
      console.log("Already sending email OTP, ignoring click");
      return;
    }

    console.log("TwoFactorQRModal: Sending email OTP");
    onSendEmailOtp(e);
  };

  return (
    <div 
      className="modal-backdrop"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050
      }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          marginTop: '-23px'
        }}
      >
        {/* Header */}
        <div style={{ 
          padding: '1.75rem',
          position: 'relative',
          borderBottom: '1px solid #e9edf1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h5 style={{ 
            margin: 0,
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: '600',
            color: '#333'
          }}>
            {viaEmail || mode === 'email' ? 'Login with email' : 'Login with QR Code'}
          </h5>
          <button
            type="button"
            className='btn-close'
            onClick={handleClose}
            style={{
              // position: 'absolute',
              // right: '16px',
              // top: '16px',
              // background: 'none',
              // border: 'none',
              // fontSize: '24px',
              // cursor: 'pointer',
              // color: '#666',
              // padding: '4px',
              // borderRadius: '4px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
          </button>
        </div>

        {/* Body */}
        <div style={{ 
          padding: '1.75rem',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '24px' }}>
            {mode === 'email' || viaEmail ? (
              <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.5', margin: '0 0 24px 0' }}>
                We will send a 6-digit verification code to your registered email. Click the button below to send or resend the email.
              </p>
            ) : (
              <>
                <p style={{ 
                  color: '#666',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  margin: '0 0 16px 0'
                }}>
                  Scan this QR code using Google Authenticator App to enable 2 Factor 
                  Authentication for your account.
                </p>
                
                <p style={{ 
                  color: '#666',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  margin: '0 0 24px 0'
                }}>
                  You can download the Google Authenticator App from Google Play 
                  Store or Apple Play Store.
                </p>
              </>
            )}
          </div>

          {/* Only show QR code in authenticator mode */}
          {mode === 'authenticator' && !viaEmail && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <QRCode value={qrCodeValue} size={180} />
            </div>
          )}

          <div>
            {/* style={{ marginBottom: '24px' }} */}
            <div style={{marginTop: 8, marginBottom: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center'}}>
              <label style={{marginRight: 12, display: 'flex', gap: '4px', alignItems: 'center'}}>
                <input 
                  type="radio" 
                  name="tf_mode" 
                  value="authenticator" 
                  checked={mode === 'authenticator' && !viaEmail} 
                  onChange={() => setMode('authenticator')} 
                  disabled={viaEmail}
                />{' '}Authenticator
              </label>
              <label style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                <input 
                  type="radio" 
                  name="tf_mode" 
                  value="email" 
                  checked={mode === 'email' || viaEmail} 
                  onChange={() => setMode('email')} 
                />{' '}Email
              </label>
            </div>

            {mode === 'authenticator' && !viaEmail && (
              <label   style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#666',
                marginBottom: '24px'
              }}>
                <input
                  type="checkbox"
                  checked={isScanned}
                  onChange={(e) => setIsScanned(e.target.checked)}
                  style={{ margin: 0 }}
                />
                Click here to confirm that you have scanned.
              </label>
            )}

            {(mode === 'email' || viaEmail) && (
              <div style={{ textAlign: 'center' }}>
                <button
                className='w-50  btn btn-primary'
                  type="button"
                  onClick={handleSendEmailOtpClick}
                  disabled={isSendingEmailOtp}
                  // style={{ 
                  //   padding: '8px 18px', 
                  //   borderRadius: 6, 
                  //   border: 'none', 
                  //   background: isSendingEmailOtp ? '#6c757d' : '#007bff', 
                  //   color: '#fff',
                  //   cursor: isSendingEmailOtp ? 'not-allowed' : 'pointer',
                  //   opacity: isSendingEmailOtp ? 0.7 : 1
                  // }}
                >
                  {isSendingEmailOtp ? (
                    <>
                      <span 
                        className="spinner-border spinner-border-sm me-2" 
                        role="status" 
                        aria-hidden="true"
                        style={{ width: '14px', height: '14px', marginRight: '8px' }}
                      ></span>
                      Sending...
                    </>
                  ) : (
                    'Send email OTP'
                  )}
                </button>
              </div>
            )}
          </div>

          {mode === 'authenticator' && !viaEmail && (
            <div style={{ 
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                type="button"
                onClick={handleClose}
                className='me-2 w-100 btn btn-secondary'
                // onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                // onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!isScanned}
                className='w-100  btn btn-primary'
                style={{
                  // backgroundColor: isScanned ? '#007bff' : '#6c757d',
                  cursor: isScanned ? 'pointer' : 'not-allowed',
                  opacity: isScanned ? 1 : 0.65
                }}
                // onMouseOver={(e) => {
                //   if (isScanned) e.target.style.backgroundColor = '#0056b3';
                // }}
                // onMouseOut={(e) => {
                //   if (isScanned) e.target.style.backgroundColor = '#007bff';
                // }}
              >
                Confirm
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorQRModal;